<template>
  <div class="profile-page">
    <h1 class="page-title">Ajustes de Perfil</h1>

    <div class="profile-layout tw-mt-4">
      <!-- Left Column: User Details Sidebar -->
      <div class="profile-sidebar">
        <div class="profile-card">
          <!-- Avatar Container with Photo and Upload Button -->
          <div class="avatar-container">
            <img
              v-if="user.photo"
              :src="getAvatarUrl(user.photo)"
              alt="Avatar"
              class="avatar-image"
              @error="onAvatarError"
            />
            <div v-else class="avatar-fallback">
              <span>{{ userInitials }}</span>
            </div>
            
            <button class="avatar-edit-btn" @click="modalImage = true" title="Cambiar foto de perfil">
              <svg class="tw-w-4 tw-h-4 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </button>
          </div>

          <!-- User Name -->
          <h2 class="user-name">{{ userFullName }}</h2>

          <!-- Role Badge -->
          <div class="roles-container">
            <span class="role-badge">
              {{ role || 'Estudiante' }}
            </span>
          </div>

          <!-- Details List -->
          <div class="details-section">
            <h3 class="details-title">Detalles</h3>
            <div class="details-list">
              <div class="detail-item">
                <span class="detail-label">Usuario:</span>
                <span class="detail-value">{{ user.username || user.name || '---' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Correo:</span>
                <span class="detail-value">{{ user.email || '---' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Estado:</span>
                <span class="detail-value text-green">Activo</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Rol:</span>
                <span class="detail-value">{{ role || 'Estudiante' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Numero de documento:</span>
                <span class="detail-value">{{ user.nro_document || 'N/A' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Contacto:</span>
                <span class="detail-value">{{ user.phone || 'N/A' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">País:</span>
                <span class="detail-value">{{ getCountryName(user.id_country) || 'N/A' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Creación:</span>
                <span class="detail-value">{{ formatDate(user.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Tabs and Forms -->
      <div class="profile-content">
        <!-- Tabs Header -->
        <div class="tabs-header">
          <button @click="activeTab = 'cuenta'" class="profile-tab-btn" :class="{ active: activeTab === 'cuenta' }">
            <svg class="tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
            <span>Cuenta</span>
          </button>
          <button @click="activeTab = 'seguridad'" class="profile-tab-btn" :class="{ active: activeTab === 'seguridad' }">
            <svg class="tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
            <span>Seguridad</span>
          </button>
          <button @click="activeTab = 'membresias'" class="profile-tab-btn" :class="{ active: activeTab === 'membresias' }">
            <svg class="tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path></svg>
            <span>Membresias</span>
          </button>
        </div>

        <!-- Tab 1: Cuenta -->
        <div v-show="activeTab === 'cuenta'" class="tab-content account-tab">
          <div class="tab-top-border"></div>
          <h3 class="form-title">Editar Perfil</h3>

          <CustomAlert :message="profileAlert.message" :type="profileAlert.type" />

          <form @submit.prevent="saveProfile" class="profile-form">
            <div class="form-grid">
              <div class="form-group">
                <label>Nombre:</label>
                <input type="text" v-model="formData.name" class="form-control" />
              </div>
              <div class="form-group">
                <label>Apellido:</label>
                <input type="text" v-model="formData.last_name" class="form-control" />
              </div>
              <div class="form-group">
                <label>Correo:</label>
                <input type="email" v-model="formData.email" class="form-control" />
              </div>
              <div class="form-group">
                <label>N° Celular:</label>
                <input type="text" v-model="formData.phone" class="form-control" />
              </div>
            </div>

            <div class="form-group full-width">
              <label>Biografia:</label>
              <textarea v-model="formData.biography" rows="4" class="form-control"></textarea>
            </div>

            <div class="form-grid-3">
              <div class="form-group">
                <label>Día de cumpleaños:</label>
                <input type="date" v-model="formData.date_birth" class="form-control" />
              </div>
              <div class="form-group">
                <label>País:</label>
                <select v-model="formData.id_country" class="form-control">
                  <option value="" disabled>Seleccione un país</option>
                  <option v-for="country in countries" :key="country.id" :value="country.id">
                    {{ country.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>Ciudad:</label>
                <input type="text" v-model="formData.city" class="form-control" />
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-save" :disabled="isSavingProfile">
                {{ isSavingProfile ? 'Guardando...' : 'Guardar Cambios' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Tab 2: Seguridad -->
        <div v-show="activeTab === 'seguridad'" class="tab-content">
          <div class="tab-top-border"></div>
          <h3 class="form-title">Cambiar Contraseña</h3>

          <CustomAlert :message="securityAlert.message" :type="securityAlert.type" />

          <form @submit.prevent="changePassword" class="profile-form">
            <div class="form-grid">
              <div class="form-group">
                <label>Nueva Contraseña</label>
                <div class="input-with-icon">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    v-model="securityData.password"
                    class="form-control"
                    placeholder="Nueva contraseña"
                  />
                  <button type="button" class="icon-btn" @click="showPassword = !showPassword">
                    <svg v-if="showPassword" class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>
                    <svg v-else class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>Confirmar Nueva Contraseña</label>
                <div class="input-with-icon">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    v-model="securityData.password_confirmation"
                    class="form-control"
                    placeholder="Confirmar contraseña"
                  />
                  <button type="button" class="icon-btn" @click="showConfirmPassword = !showConfirmPassword">
                    <svg v-if="showConfirmPassword" class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path></svg>
                    <svg v-else class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                  </button>
                </div>
              </div>
            </div>

            <p class="text-muted" style="font-size: 13px; margin-top: 8px;">
              Mínimo 8 caracteres de largo, mayúsculas y símbolos
            </p>

            <div class="form-actions">
              <button type="submit" class="btn-save" style="margin-top: 8px;" :disabled="isChangingPassword">
                {{ isChangingPassword ? 'Actualizando...' : 'Cambiar Contraseña' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Tab 3: Membresias -->
        <div v-show="activeTab === 'membresias'" class="tab-content membership-tab">
          <div class="tab-top-border"></div>

          <CustomAlert :message="profileAlert.message" :type="profileAlert.type" />

          <!-- Tarjeta VIP 3D Flip -->
          <VipMembershipCard :membership="activeMembership" :user="user" />

          <!-- Progreso de días de membresía -->
          <div class="membership-progress-container">
            <div class="progress-labels">
              <span>Días</span>
              <span>{{ membershipStatus.labelRight }}</span>
            </div>
            <div class="progress-bar-bg">
              <div
                class="progress-bar-fill"
                :style="{
                  width: membershipStatus.percent + '%',
                  backgroundColor: membershipStatus.isExpired ? '#ef4444' : 'var(--primary-color, #18d600)',
                }"
              ></div>
            </div>
            <p class="text-muted" style="font-size: 13px; margin-top: 8px;">{{ membershipStatus.text }}</p>
          </div>

          <!-- Acciones de Membresía -->
          <div class="membership-actions">
            <AnimatedButton @click="showUpgradeModal = true">
              {{ membershipStatus.isExpired ? 'Renovar Membresía' : 'Mejorar Membresía' }}
            </AnimatedButton>
            <AnimatedButton @click="openHistoryModal">
              Historial de membresía
            </AnimatedButton>
          </div>

          <!-- Modal: Upgrade / Renovar -->
          <div v-if="showUpgradeModal" class="custom-modal-overlay" @click.self="showUpgradeModal = false">
            <div class="custom-modal-content fade-in-up">
              <div class="modal-header">
                <h4>{{ membershipStatus.isExpired ? 'Renueva tu membresía' : 'Adquiere una nueva membresía' }}</h4>
                <button class="close-btn" @click="showUpgradeModal = false">
                  <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div class="modal-body">
                <p v-if="upgradeableMemberships.length === 0" class="text-muted">
                  No hay planes superiores disponibles en este momento.
                </p>
                <div class="table-responsive" v-else>
                  <table class="custom-table">
                    <thead>
                      <tr>
                        <th>Membresía</th>
                        <th>Precio</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="plan in upgradeableMemberships" :key="plan.id">
                        <td><strong>{{ plan.account }}</strong></td>
                        <td>
                          <span v-if="membershipStatus.isExpired || !activeMembership">
                            ${{ parseFloat(plan.price).toFixed(2) }} + IVA ({{ plan.iva }}%)
                          </span>
                          <span v-else>
                            ${{ (parseFloat(plan.price) - parseFloat(activeMembership.price)).toFixed(2) }}
                            <small class="text-muted" style="display:block; font-size: 11px;">(Diferencia a pagar)</small>
                          </span>
                        </td>
                        <td>
                          <button class="btn-select-plan" @click="selectPlan(plan)">Seleccionar</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal: Historial de Membresías -->
          <div v-if="showHistoryModal" class="custom-modal-overlay" @click.self="showHistoryModal = false">
            <div class="custom-modal-content fade-in-up">
              <div class="modal-header">
                <h4>Historial de Membresías</h4>
                <button class="close-btn" @click="showHistoryModal = false">
                  <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div class="modal-body">
                <div class="table-responsive">
                  <table class="custom-table">
                    <thead>
                      <tr>
                        <th>Fecha Compra</th>
                        <th>Fecha Expiración</th>
                        <th>Plan</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loadingHistory">
                        <td colspan="4" class="text-center" style="padding: 24px;">
                          <div class="spinner"></div>
                          <span style="display: block; margin-top: 8px; font-size: 13px; color: var(--text-muted);">Cargando historial...</span>
                        </td>
                      </tr>
                      <tr v-else-if="membershipHistory.length === 0">
                        <td colspan="4" class="text-center text-muted" style="padding: 24px;">
                          No se encontraron registros de membresías pasadas.
                        </td>
                      </tr>
                      <tr v-for="record in membershipHistory" :key="record.id" v-else>
                        <td>{{ formatHistoryDate(record.purchase_date || record.created_at) }}</td>
                        <td>{{ formatHistoryDate(record.expiration_date) }}</td>
                        <td>
                          <span class="status-badge plan-badge">
                            {{ record.account_type_name }}
                          </span>
                        </td>
                        <td>
                          <span :class="getHistoryStatus(record).class">
                            {{ getHistoryStatus(record).text }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal: Pago de Membresía -->
          <div v-if="showPaymentModal" class="custom-modal-overlay" @click.self="showPaymentModal = false">
            <div class="custom-modal-content fade-in-up" style="max-width: 450px;">
              <div class="modal-header">
                <h4 style="color: var(--primary-color, #18d600);">Membresía</h4>
                <button class="close-btn" @click="showPaymentModal = false">
                  <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div class="modal-body" style="padding: 24px;">
                <CustomAlert :message="paymentAlert.message" :type="paymentAlert.type" />
                
                <div class="form-group" style="margin-bottom: 20px;">
                  <select v-model="selectedPaymentMethod" class="form-control" @change="paymentAlert.message = ''">
                    <option value="" disabled>Seleccione método de pago</option>
                    <option value="wallet">Billetera (Saldo: ${{ walletBalance.toFixed(2) }})</option>
                    <option value="card">Tarjeta crédito / débito</option>
                  </select>
                </div>

                <p style="margin-bottom: 16px; font-size: 14px; color: var(--text-bold);">
                  Ha seleccionado la membresía <strong>{{ selectedPlanToBuy ? selectedPlanToBuy.account : '' }}</strong>
                </p>

                <!-- Resumen de Billetera -->
                <div v-if="selectedPaymentMethod === 'wallet'" class="wallet-info-box" style="margin-bottom: 20px; padding: 16px; background-color: rgba(24, 214, 0, 0.06); border-left: 4px solid var(--primary-color, #18d600); border-radius: 8px;">
                  <h5 style="margin-top: 0; margin-bottom: 10px; color: var(--primary-color, #18d600); font-weight: 700;">Resumen de Billetera</h5>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px;">
                    <span class="text-muted">Saldo actual:</span>
                    <span style="color: var(--text-bold); font-weight: 600;">${{ walletBalance.toFixed(2) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px;">
                    <span class="text-muted">Monto a pagar:</span>
                    <span style="color: #ef4444; font-weight: 600;">-${{ amountToPay.toFixed(2) }}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--border-color); font-weight: bold; font-size: 14px;">
                    <span>Saldo restante:</span>
                    <span :style="{ color: isWalletSufficient ? 'var(--primary-color, #18d600)' : '#ef4444' }">
                      ${{ (walletBalance - amountToPay).toFixed(2) }}
                    </span>
                  </div>
                  <p v-if="!isWalletSufficient" style="color: #ef4444; font-size: 12px; margin-top: 8px; margin-bottom: 0;">
                    Saldo insuficiente en billetera para esta compra.
                  </p>
                </div>

                <div class="terms-toggle-wrapper" style="display: flex; align-items: center; gap: 10px; margin-bottom: 20px;">
                  <input type="checkbox" id="profile-terms-check" v-model="acceptTerms" class="tw-rounded tw-text-[#18d600] focus:tw-ring-[#18d600]" />
                  <label for="profile-terms-check" style="font-size: 13px; color: var(--text-muted); cursor: pointer;">
                    Acepto los términos y condiciones de la membresía
                  </label>
                </div>

                <button
                  type="button"
                  class="btn-save tw-w-full"
                  :disabled="isProcessingPayment || (selectedPaymentMethod === 'wallet' && !isWalletSufficient)"
                  @click="processPayment"
                >
                  {{ isProcessingPayment ? 'Procesando...' : 'Confirmar Pago' }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Modal: Cambiar foto de perfil -->
    <div v-if="modalImage" class="custom-modal-overlay" @click.self="onCancel">
      <div class="custom-modal-content fade-in-up" style="max-width: 480px;">
        <div class="modal-header">
          <h4>Actualizar Foto de Perfil</h4>
          <button class="close-btn" @click="onCancel">
            <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
        <div class="modal-body" style="padding: 24px;">
          <div class="tw-flex tw-justify-center tw-mb-6">
            <div class="tw-w-[130px] tw-h-[130px] tw-rounded-full tw-overflow-hidden tw-border-4 tw-border-[var(--primary-color)] tw-shadow-md">
              <img v-if="pictureModal" :src="pictureModal" class="tw-w-full tw-h-full tw-object-cover" alt="Previsualización" />
              <img v-else-if="user.photo" :src="getAvatarUrl(user.photo)" class="tw-w-full tw-h-full tw-object-cover" alt="Actual" />
              <div v-else class="avatar-fallback tw-w-full tw-h-full">
                <span>{{ userInitials }}</span>
              </div>
            </div>
          </div>

          <div class="form-group" style="margin-bottom: 20px;">
            <label>Seleccionar Imagen</label>
            <input type="file" accept="image/*" @change="onFileSelected" class="form-control" />
          </div>

          <div class="tw-flex tw-justify-end tw-gap-3">
            <button type="button" @click="onCancel" class="btn-cancel">Cancelar</button>
            <button type="button" @click="onUpload" :disabled="loadingSaveImage || !file" class="btn-save">
              {{ loadingSaveImage ? 'Subiendo...' : 'Guardar Foto' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import VipMembershipCard from "@/components/VipMembershipCard.vue";
import AnimatedButton from "@/components/AnimatedButton.vue";
import CustomAlert from "@/components/CustomAlert.vue";
import { authSet } from "@/helpers/authStorage";

export default {
  name: "perfil",
  components: {
    VipMembershipCard,
    AnimatedButton,
    CustomAlert,
  },
  data() {
    return {
      activeTab: "cuenta",
      user: {},
      role: "",
      formData: {
        name: "",
        last_name: "",
        email: "",
        phone: "",
        biography: "",
        date_birth: "",
        id_country: "",
        city: "",
      },
      securityData: {
        password: "",
        password_confirmation: "",
      },
      showPassword: false,
      showConfirmPassword: false,
      countries: [],
      availableMemberships: [],
      realWalletBalance: 0,
      
      // Alerts
      profileAlert: { message: "", type: "error" },
      securityAlert: { message: "", type: "error" },
      paymentAlert: { message: "", type: "error" },
      
      // Modals
      showUpgradeModal: false,
      showHistoryModal: false,
      showPaymentModal: false,
      modalImage: false,
      
      loadingHistory: false,
      membershipHistory: [],
      selectedPlanToBuy: null,
      selectedPaymentMethod: "",
      acceptTerms: false,
      isProcessingPayment: false,
      isSavingProfile: false,
      isChangingPassword: false,
      
      // Photo Upload
      file: null,
      pictureModal: null,
      loadingSaveImage: false,
      defaultAvatar: "https://promolider-storage-user.s3-accelerate.amazonaws.com/default-avatar.png",
    };
  },
  computed: {
    userFullName() {
      if (!this.user) return "Usuario";
      const name = this.user.name || "";
      const lastName = this.user.last_name || "";
      return `${name} ${lastName}`.trim() || "Usuario";
    },
    userInitials() {
      const name = this.userFullName;
      const parts = name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },
    activeMembership() {
      if (!this.availableMemberships.length || !this.user) return null;
      return this.availableMemberships.find((m) => m.id === this.user.id_account_type) || null;
    },
    membershipStatus() {
      if (!this.user || !this.user.expiration_membership_date) {
        return {
          daysLeft: 0,
          percent: 0,
          text: "Su membresía ha expirado o no tiene fecha límite",
          labelRight: "Expirado",
          isExpired: true,
        };
      }

      const expireDate = new Date(this.user.expiration_membership_date);
      const now = new Date();
      const diffTime = expireDate - now;
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (daysLeft <= 0) {
        return {
          daysLeft: 0,
          percent: 0,
          text: "Su membresía ha expirado",
          labelRight: "Expirado",
          isExpired: true,
        };
      }

      const percent = Math.min((daysLeft / 365) * 100, 100);

      return {
        daysLeft,
        percent,
        text: `Le quedan ${daysLeft} días de membresía`,
        labelRight: `${daysLeft} días`,
        isExpired: false,
      };
    },
    upgradeableMemberships() {
      if (!this.availableMemberships) return [];
      const isCurrentlyExpired = this.membershipStatus.isExpired;

      if (isCurrentlyExpired) {
        return this.availableMemberships.filter((plan) => parseFloat(plan.price) > 0);
      } else {
        const currentPrice = this.activeMembership ? parseFloat(this.activeMembership.price) : 0;
        return this.availableMemberships.filter((plan) => parseFloat(plan.price) > currentPrice);
      }
    },
    walletBalance() {
      return this.realWalletBalance ? parseFloat(this.realWalletBalance) : 0;
    },
    amountToPay() {
      if (!this.selectedPlanToBuy) return 0;
      let baseAmount = 0;
      if (this.membershipStatus.isExpired || !this.activeMembership) {
        baseAmount = parseFloat(this.selectedPlanToBuy.price);
      } else {
        baseAmount = parseFloat(this.selectedPlanToBuy.price) - parseFloat(this.activeMembership.price);
      }
      const ivaPercentage = this.selectedPlanToBuy.iva ? parseFloat(this.selectedPlanToBuy.iva) : 18;
      const total = baseAmount * (1 + ivaPercentage / 100);
      return total > 0 ? total : 0;
    },
    isWalletSufficient() {
      return this.walletBalance >= this.amountToPay;
    },
  },
  async created() {
    await this.loadUserProfile();
    await this.loadRole();
    await this.loadCountries();
    await this.loadMemberships();
    await this.loadWalletBalance();
  },
  methods: {
    showProfileAlert(msg, type = "error") {
      this.profileAlert = { message: msg, type };
      setTimeout(() => { this.profileAlert.message = ""; }, 6000);
    },
    showSecurityAlert(msg, type = "error") {
      this.securityAlert = { message: msg, type };
      setTimeout(() => { this.securityAlert.message = ""; }, 6000);
    },
    showPaymentAlert(msg, type = "error") {
      this.paymentAlert = { message: msg, type };
    },
    async loadUserProfile() {
      try {
        const res = await this.axios.get("/profile/info");
        const u = res.data?.user || res.data?.data || res.data;
        if (u) {
          this.user = u;
          this.formData = {
            name: u.name || "",
            last_name: u.last_name || "",
            email: u.email || "",
            phone: u.phone || "",
            biography: u.biography || "",
            date_birth: u.date_birth ? u.date_birth.split("T")[0] : "",
            id_country: u.id_country || "",
            city: u.city || "",
          };
          if (u.name) authSet("name_user", u.name);
          if (u.last_name) authSet("last_name_user", u.last_name);
          if (u.email) authSet("email_user", u.email);
          if (u.photo) {
            authSet("photo_user", u.photo);
            window.dispatchEvent(new Event("user-avatar-updated"));
          }
        }
      } catch (e) {
        console.error("Error cargando perfil:", e);
      }
    },
    async loadRole() {
      try {
        const response = await this.axios.get("/dashboard/getattributes");
        if (response.data && response.data.data) {
          this.role = response.data.data.role;
        }
      } catch (error) {
        this.role = "Estudiante";
      }
    },
    async loadCountries() {
      try {
        const res = await this.axios.get("/countries");
        this.countries = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Error cargando países:", e);
      }
    },
    async loadMemberships() {
      try {
        const res = await this.axios.get("/memberships");
        this.availableMemberships = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Error cargando membresías:", e);
      }
    },
    async loadWalletBalance() {
      try {
        const res = await this.axios.get("/marketing/reports/wallet/balance");
        this.realWalletBalance = res.data?.total_balance || 0;
      } catch (e) {
        this.realWalletBalance = 0;
      }
    },
    getCountryName(id) {
      if (!id) return "";
      const c = this.countries.find((item) => item.id == id);
      return c ? c.name : id;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      } catch (e) {
        return dateString;
      }
    },
    formatHistoryDate(dateString) {
      if (!dateString) return "-";
      try {
        const d = new Date(dateString);
        return d.toLocaleDateString();
      } catch (e) {
        return dateString;
      }
    },
    getAvatarUrl(photoPath) {
      if (!photoPath) return "";
      if (photoPath.startsWith("http")) return photoPath;
      return `https://promolider-storage-user.s3.sa-east-1.amazonaws.com/${photoPath}`;
    },
    onAvatarError(e) {
      if (e && e.target) {
        e.target.style.display = "none";
      }
    },
    async saveProfile() {
      this.isSavingProfile = true;
      this.profileAlert.message = "";
      try {
        const res = await this.axios.put("/profile/update", this.formData);
        this.showProfileAlert("Perfil actualizado exitosamente", "success");
        if (res.data && res.data.user) {
          this.user = { ...this.user, ...res.data.user };
          authSet("name_user", res.data.user.name || "");
          authSet("last_name_user", res.data.user.last_name || "");
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.response?.data?.error || "Error al actualizar el perfil";
        this.showProfileAlert(msg, "error");
      } finally {
        this.isSavingProfile = false;
      }
    },
    async changePassword() {
      if (!this.securityData.password || !this.securityData.password_confirmation) {
        this.showSecurityAlert("Todos los campos son requeridos", "error");
        return;
      }
      if (this.securityData.password.length < 8) {
        this.showSecurityAlert("La contraseña debe tener como mínimo 8 caracteres", "error");
        return;
      }
      if (this.securityData.password !== this.securityData.password_confirmation) {
        this.showSecurityAlert("Las contraseñas no coinciden", "error");
        return;
      }

      this.isChangingPassword = true;
      this.securityAlert.message = "";
      try {
        await this.axios.put("/profile/password", this.securityData);
        this.showSecurityAlert("Contraseña actualizada exitosamente", "success");
        this.securityData.password = "";
        this.securityData.password_confirmation = "";
      } catch (error) {
        if (error.response?.data?.errors) {
          const firstErr = Object.values(error.response.data.errors)[0][0];
          this.showSecurityAlert(firstErr, "error");
        } else {
          this.showSecurityAlert(error.response?.data?.message || "Error al actualizar contraseña.", "error");
        }
      } finally {
        this.isChangingPassword = false;
      }
    },
    async openHistoryModal() {
      this.showHistoryModal = true;
      this.loadingHistory = true;
      try {
        const res = await this.axios.get("/profile/membership-history");
        this.membershipHistory = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.membershipHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    getHistoryStatus(record) {
      if (record.status != 1) {
        return { text: "Fallido/Cancelado", class: "status-badge danger" };
      }
      const exp = new Date(record.expiration_date);
      if (exp < new Date()) {
        return { text: "Expirado", class: "status-badge danger" };
      }
      return { text: "Activo", class: "status-badge success" };
    },
    selectPlan(plan) {
      this.selectedPlanToBuy = plan;
      this.showUpgradeModal = false;
      this.selectedPaymentMethod = "";
      this.acceptTerms = false;
      this.paymentAlert.message = "";
      this.showPaymentModal = true;
    },
    async processPayment() {
      this.paymentAlert.message = "";
      if (!this.acceptTerms) {
        this.showPaymentAlert("Debe aceptar los términos y condiciones antes de continuar.", "warning");
        return;
      }
      if (!this.selectedPaymentMethod) {
        this.showPaymentAlert("Por favor seleccione un método de pago", "warning");
        return;
      }

      if (this.selectedPaymentMethod === "wallet") {
        if (!this.isWalletSufficient) {
          this.showPaymentAlert("Saldo insuficiente en tu billetera.", "error");
          return;
        }
        this.isProcessingPayment = true;
        try {
          const res = await this.axios.post("/marketing/membership/purchase-wallet", {
            plan_id: this.selectedPlanToBuy.id,
          });
          if (res.data?.success) {
            this.showPaymentAlert("¡Transacción aprobada! Actualizando membresía...", "success");
            setTimeout(async () => {
              await this.loadUserProfile();
              await this.loadWalletBalance();
              this.showPaymentModal = false;
              this.showProfileAlert("¡Membresía actualizada con éxito!", "success");
              this.isProcessingPayment = false;
            }, 1500);
          }
        } catch (e) {
          this.isProcessingPayment = false;
          this.showPaymentAlert(e.response?.data?.error || "Error al procesar el pago con billetera", "error");
        }
      } else {
        this.showProfileAlert(`Procesando pago para ${this.selectedPlanToBuy?.account} con Tarjeta.`, "info");
        this.showPaymentModal = false;
      }
    },
    onFileSelected(e) {
      const file = e?.target?.files?.[0];
      if (file) {
        this.file = file;
        this.pictureModal = URL.createObjectURL(file);
      }
    },
    onCancel() {
      this.modalImage = false;
      this.file = null;
      this.pictureModal = null;
    },
    async onUpload() {
      if (!this.file) return;
      this.loadingSaveImage = true;
      const formData = new FormData();
      formData.append("photo", this.file);
      try {
        const res = await this.axios.post("/profile/upload-photo", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.photo) {
          this.user.photo = res.data.photo;
          authSet("photo_user", res.data.photo);
          window.dispatchEvent(new Event("user-avatar-updated"));
        }
        this.onCancel();
        this.showProfileAlert("Foto de perfil actualizada", "success");
      } catch (e) {
        // Fallback: Si no hay endpoint dedicado para upload-photo
        this.showProfileAlert("La foto se guardará con la actualización de perfil", "info");
        this.onCancel();
      } finally {
        this.loadingSaveImage = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-page {
  padding: 8px 12px 48px 12px;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: var(--text-bold, #111827);
  margin-bottom: 20px;
  font-family: 'Outfit', sans-serif;
  letter-spacing: -0.5px;
}

.profile-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

@media (min-width: 992px) {
  .profile-layout {
    flex-direction: row;
    align-items: flex-start;
  }
}

/* Sidebar */
.profile-sidebar {
  width: 100%;
}

@media (min-width: 992px) {
  .profile-sidebar {
    width: 320px;
    flex-shrink: 0;
  }
}

.profile-content {
  width: 100%;
  flex: 1;
  min-width: 0;
}

.profile-card {
  background: var(--card-bg, #ffffff);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid var(--border-color, #e5e7eb);
  backdrop-filter: blur(10px);
}

.avatar-container {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: linear-gradient(135deg, #18d600, #0db800);
  margin-bottom: 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(24, 214, 0, 0.25);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-fallback {
  font-size: 36px;
  font-weight: 800;
  color: white;
  font-family: 'Outfit', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-edit-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: var(--primary-color, #18d600);
  border: 3px solid var(--card-bg, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.user-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-bold, #111827);
  margin-bottom: 8px;
  font-family: 'Outfit', sans-serif;
}

.roles-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 8px;
}

.role-badge {
  background-color: rgba(24, 214, 0, 0.12);
  color: var(--primary-color, #18d600);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 9999px;
  border: 1px solid rgba(24, 214, 0, 0.25);
}

.details-section {
  width: 100%;
  margin-top: 20px;
  border-top: 1px solid var(--border-color, #e5e7eb);
  padding-top: 20px;
  text-align: left;
}

.details-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-bold, #111827);
  margin-bottom: 16px;
  font-family: 'Outfit', sans-serif;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 13px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  color: var(--text-muted, #6b7280);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  color: var(--text-bold, #111827);
  font-weight: 600;
  font-size: 13px;
  word-break: break-all;
}

.text-green {
  color: var(--primary-color, #18d600) !important;
  font-weight: 700;
}

/* Content Area */
.profile-content {
  width: 100%;
  flex-grow: 1;
}

.tabs-header {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  padding-bottom: 4px;
}

.profile-tab-btn {
  padding: 10px 22px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: var(--text-muted, #64748b);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Outfit', sans-serif;
}

.profile-tab-btn .tab-icon {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  flex-shrink: 0;
}

.profile-tab-btn:hover {
  background: var(--card-sub-bg, rgba(128, 128, 128, 0.1));
  color: var(--text-bold, #111827);
}

.profile-tab-btn.active {
  background: var(--primary-color, #18d600) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 16px rgba(24, 214, 0, 0.35);
}

.profile-tab-btn.active .tab-icon,
.profile-tab-btn.active svg,
.profile-tab-btn.active span {
  color: #ffffff !important;
  stroke: #ffffff !important;
}

.tab-content {
  background: var(--card-bg, #ffffff);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-color, #e5e7eb);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.tab-top-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #18d600, #0db800);
}

.form-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-bold, #111827);
  margin-bottom: 24px;
  font-family: 'Outfit', sans-serif;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.form-grid-3 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .form-grid-3 {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted, #6b7280);
  margin-bottom: 6px;
}

.form-control {
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  outline: none;
  background: var(--input-bg, transparent);
  color: var(--text-bold, #111827);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color, #18d600);
  box-shadow: 0 0 0 3px rgba(24, 214, 0, 0.15);
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-icon .form-control {
  width: 100%;
  padding-right: 42px;
}

.icon-btn {
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn:hover {
  color: var(--primary-color, #18d600);
}

.form-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 10px;
}

.btn-save {
  padding: 12px 28px;
  background: var(--primary-color, #18d600);
  color: white;
  font-weight: 700;
  font-size: 14px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(24, 214, 0, 0.3);
}

.btn-save:hover:not(:disabled) {
  background: var(--primary-hover, #16bf00);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(24, 214, 0, 0.4);
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  padding: 10px 20px;
  background: transparent;
  color: var(--text-muted, #6b7280);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.btn-cancel:hover {
  background: var(--card-sub-bg, rgba(128, 128, 128, 0.08));
}

/* Membership Tab Specifics */
.membership-progress-container {
  margin: 28px 0 20px 0;
  padding: 20px;
  background: var(--card-sub-bg, rgba(128, 128, 128, 0.05));
  border-radius: 14px;
  border: 1px solid var(--border-color, #e5e7eb);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-bold, #111827);
  margin-bottom: 8px;
}

.progress-bar-bg {
  width: 100%;
  height: 10px;
  background: var(--border-color, #e5e7eb);
  border-radius: 9999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.membership-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
}

/* Modals */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.custom-modal-content {
  background: var(--card-bg, #ffffff);
  border-radius: 20px;
  width: 100%;
  max-width: 650px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
  border: 1px solid var(--border-color, #e5e7eb);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.modal-header h4 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--text-bold, #111827);
  font-family: 'Outfit', sans-serif;
}

.close-btn {
  background: transparent;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
}

.close-btn:hover {
  background: var(--card-sub-bg, rgba(128, 128, 128, 0.1));
  color: var(--text-bold, #111827);
}

.modal-body {
  padding: 20px 24px;
}

.table-responsive {
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.custom-table th, .custom-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.custom-table th {
  color: var(--text-muted, #6b7280);
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
}

.custom-table td {
  color: var(--text-bold, #111827);
}

.btn-select-plan {
  padding: 6px 14px;
  background: var(--primary-color, #18d600);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-select-plan:hover {
  opacity: 0.9;
}

.status-badge {
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  display: inline-block;
}

.status-badge.success {
  background: rgba(22, 163, 74, 0.15);
  color: #16a34a;
}

.status-badge.danger {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
}

.plan-badge {
  background: rgba(24, 214, 0, 0.12);
  color: var(--primary-color, #18d600);
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(24, 214, 0, 0.2);
  border-top-color: var(--primary-color, #18d600);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.fade-in-up {
  animation: fadeInUp 0.25s ease-out;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Dark Mode Overrides */
/* Dark Mode Overrides */
.dark .profile-card,
.dark-mode .profile-card,
.dark .tab-content,
.dark-mode .tab-content,
.dark .custom-modal-content,
.dark-mode .custom-modal-content {
  background: #161e2e !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.dark .profile-tab-btn,
.dark-mode .profile-tab-btn {
  color: #94a3b8 !important;
}

.dark .profile-tab-btn .tab-icon,
.dark-mode .profile-tab-btn .tab-icon {
  stroke: #94a3b8 !important;
}

.dark .profile-tab-btn:hover,
.dark-mode .profile-tab-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #f8fafc !important;
}

.dark .profile-tab-btn:hover .tab-icon,
.dark-mode .profile-tab-btn:hover .tab-icon {
  stroke: #f8fafc !important;
}

.dark .profile-tab-btn.active,
.dark-mode .profile-tab-btn.active {
  background: var(--primary-color, #18d600) !important;
  color: #ffffff !important;
}

.dark .profile-tab-btn.active .tab-icon,
.dark-mode .profile-tab-btn.active .tab-icon,
.dark .profile-tab-btn.active svg,
.dark-mode .profile-tab-btn.active svg,
.dark .profile-tab-btn.active span,
.dark-mode .profile-tab-btn.active span {
  color: #ffffff !important;
  stroke: #ffffff !important;
}

.dark .form-control,
.dark-mode .form-control {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #f3f4f6 !important;
  background: #0f172a !important;
}

.dark .form-control option,
.dark-mode .form-control option {
  background: #0f172a !important;
  color: #f3f4f6 !important;
}

.dark .form-control:focus,
.dark-mode .form-control:focus {
  border-color: #18d600 !important;
}

.dark .page-title,
.dark-mode .page-title,
.dark .user-name,
.dark-mode .user-name,
.dark .details-title,
.dark-mode .details-title,
.dark .detail-value,
.dark-mode .detail-value,
.dark .form-title,
.dark-mode .form-title,
.dark .modal-header h4,
.dark-mode .modal-header h4,
.dark .custom-table td,
.dark-mode .custom-table td,
.dark .progress-labels,
.dark-mode .progress-labels {
  color: #f3f4f6 !important;
}

.dark .detail-label,
.dark-mode .detail-label,
.dark .form-group label,
.dark-mode .form-group label,
.dark .text-muted,
.dark-mode .text-muted,
.dark .custom-table th,
.dark-mode .custom-table th {
  color: #94a3b8 !important;
}

.dark .membership-progress-container,
.dark-mode .membership-progress-container {
  background: rgba(15, 23, 42, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.dark .progress-bar-bg,
.dark-mode .progress-bar-bg {
  background: #334155 !important;
}

.dark .custom-table th,
.dark-mode .custom-table th,
.dark .custom-table td,
.dark-mode .custom-table td,
.dark .modal-header,
.dark-mode .modal-header,
.dark .details-section,
.dark-mode .details-section,
.dark .tabs-header,
.dark-mode .tabs-header {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
